import { makeAutoObservable } from 'mobx';
import serviceViewModel from './company/service-view-model';
import { bookingViewModelMobX } from './booking-view-model';
import toastStore from '../../store/toast-store';
import { BookingCreateParams } from '../../model/professional/booking-model';
import companyStore from "../../store/company-store";
import promoCodeViewModel from './promo-code/promo-code-view-model';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectedServiceItem {
  service: any;
  optionIds: string[];
}

class ReservationViewModel {
  // Instances privées des services
  private serviceViewModel;
  private bookingViewModel;

  // États du formulaire
  selectedService: string = '';
  selectedCollaboratorCompagnieId: string = '';
  selectedOptions: {[categoryId: string]: string[]} = {};
  selectedDate: string = '';

  // Multi-services
  selectedServices: SelectedServiceItem[] = [];

  // Booking manuel : horaires personnalisés
  manualStartTime: string = '';
  manualEndTime: string = '';

  clientFirstName: string = '';
  clientLastName: string = '';
  clientEmail: string = '';
  clientPhone: string = '';
  note: string = '';

  // Code promo
  promoCode: string = '';
  promoCodeValidated: boolean = false;
  promoCodeDiscount: number = 0;
  promoCodeMessage: string = '';
  isValidatingPromoCode: boolean = false;

  // Données chargées
  services: any[] = [];
  serviceDetails: any = null;
  optionCategories: any[] = [];
  serviceSelectOptions: SelectOption[] = [];

  // États de chargement
  isSubmitting: boolean = false;

  // Mode édition
  isEditMode: boolean = false;
  bookingIdToEdit: string | null = null;

  // Propriété calculée pour calculer la durée totale de tous les services sélectionnés
  get totalDuration(): number {
    let total = 0;

    for (const item of this.selectedServices) {
      const serviceDuration = item.service.duration || 0;
      let optionsDuration = 0;

      // Calculer la durée des options de ce service
      if (item.service.optionCategories) {
        for (const category of item.service.optionCategories) {
          if (category.options) {
            for (const option of category.options) {
              if (item.optionIds.includes(option.id) && option.durationMinutes) {
                optionsDuration += option.durationMinutes;
              }
            }
          }
        }
      }

      total += serviceDuration + optionsDuration;
    }

    return total;
  }

  // Propriété calculée pour le prix total
  get totalPrice(): number {
    let total = 0;

    for (const item of this.selectedServices) {
      const servicePrice = typeof item.service.price === 'number'
        ? item.service.price
        : parseFloat(String(item.service.price || 0));
      let optionsPrice = 0;

      // Calculer le prix des options de ce service
      if (item.service.optionCategories) {
        for (const category of item.service.optionCategories) {
          if (category.options) {
            for (const option of category.options) {
              if (item.optionIds.includes(option.id)) {
                const optionPrice = typeof option.price === 'number'
                  ? option.price
                  : parseFloat(String(option.price || 0));
                optionsPrice += optionPrice;
              }
            }
          }
        }
      }

      total += servicePrice + optionsPrice;
    }

    return total;
  }

  // Propriété calculée pour le prix final après réduction
  get finalPrice(): number {
    return Math.max(0, this.totalPrice - this.promoCodeDiscount);
  }

  setIsSubmitting(value: boolean) {
    this.isSubmitting = value;
  }

  setServices(services: any[]) {
    this.services = services;
  }

  setServiceSelectOptions(options: SelectOption[]) {
    this.serviceSelectOptions = options;
  }

  setServiceDetails(details: any) {
    this.serviceDetails = details;
  }

  setOptionCategories(categories: any[]) {
    this.optionCategories = categories;
  }

  setSelectedDate(date: string) {
    this.selectedDate = date;
  }

  setManualStartTime(time: string) {
    this.manualStartTime = time;
  }

  setManualEndTime(time: string) {
    this.manualEndTime = time;
  }

  setClientFirstName = (firstName: string) => {
    this.clientFirstName = firstName;
  };

  setClientLastName = (lastName: string) => {
    this.clientLastName = lastName;
  };

  setClientEmail = (email: string) => {
    this.clientEmail = email;
  };

  setClientPhone = (phone: string) => {
    this.clientPhone = phone;
  };

  setNote = (note: string) => {
    this.note = note;
  };

  setSelectedCollaboratorCompagnieId = (collaboratorCompagnieId: string) => {
    this.selectedCollaboratorCompagnieId = collaboratorCompagnieId;
  };

  setOptionCheckboxValue(categoryId: string, optionId: string, checked: boolean) {
    const current = this.selectedOptions[categoryId] || [];

    if (checked) {
      if (!current.includes(optionId)) {
        this.selectedOptions = { ...this.selectedOptions, [categoryId]: [...current, optionId] };
      }
    } else {
      this.selectedOptions = { ...this.selectedOptions, [categoryId]: current.filter(id => id !== optionId) };
    }
  }

  setOptionSelectValue(categoryId: string, optionId: string) {
    this.selectedOptions[categoryId] = [optionId];
  }

  constructor() {
    this.serviceViewModel = serviceViewModel;
    this.bookingViewModel = bookingViewModelMobX;
    makeAutoObservable(this);
  }

  async loadServices() {
    try {
      const response = await this.serviceViewModel.getServices();
      if (response) {
        this.setServices(response);

        const options = response
          .filter((service: any) => service.isActive !== false)
          .map((service: any) => ({
            value: service.id,
            label: service.name
          }));
        this.setServiceSelectOptions(options);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
      toastStore.error('Impossible de charger les services. Veuillez réessayer.');
    }
  }

  setSelectedService(serviceId: string) {
    this.selectedService = serviceId;
    this.selectedOptions = {};

    if (serviceId) {
      this.loadServiceDetails(serviceId);
    } else {
      this.serviceDetails = null;
      this.optionCategories = [];
    }
  }

  async loadServiceDetails(serviceId: string) {
    try {
      if (serviceId) {
        const serviceWithDetails = await this.serviceViewModel.getServiceById(serviceId);

        if (serviceWithDetails && serviceWithDetails.isActive !== false) {
          this.setServiceDetails(serviceWithDetails);

          let categories = [];
          if (serviceWithDetails.optionCategories && Array.isArray(serviceWithDetails.optionCategories)) {
            categories = serviceWithDetails.optionCategories
              .filter((category: any) => category.isActive !== false)
              .map((category: any) => ({
                ...category,
                options: category.options ?
                  category.options.filter((option: any) => option.isActive !== false) :
                  []
              }));
          }
          this.setOptionCategories(categories);
        } else {
          this.selectedService = '';
          this.setServiceDetails(null);
          this.setOptionCategories([]);
          toastStore.error("Ce service n'est plus disponible.");
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails du service:', error);
      toastStore.error('Impossible de charger les détails du service. Veuillez réessayer.');
    }
  }

  /**
   * Ajoute le service actuellement sélectionné avec ses options à la liste des services
   */
  addServiceToBooking() {
    if (!this.serviceDetails) {
      toastStore.error('Veuillez sélectionner un service.');
      return;
    }

    // Vérifier que toutes les catégories requises ont des options sélectionnées
    const requiredCategoriesSelected = this.optionCategories
      .filter(category => category.required)
      .every(category => {
        if (category.allowMultiple) {
          return this.selectedOptions[category.id]?.length > 0;
        } else {
          return this.selectedOptions[category.id]?.length === 1;
        }
      });

    if (!requiredCategoriesSelected) {
      toastStore.error('Veuillez sélectionner toutes les options obligatoires.');
      return;
    }

    // Collecter tous les IDs d'options sélectionnées
    const optionIds: string[] = [];
    Object.values(this.selectedOptions).forEach((ids) => {
      optionIds.push(...ids);
    });

    // Ajouter le service à la liste
    this.selectedServices.push({
      service: this.serviceDetails,
      optionIds
    });

    // Réinitialiser la sélection pour le prochain service
    this.selectedService = '';
    this.serviceDetails = null;
    this.optionCategories = [];
    this.selectedOptions = {};
  }

  /**
   * Retire un service de la liste des services sélectionnés
   */
  removeServiceFromBooking(index: number) {
    this.selectedServices.splice(index, 1);
    // Réinitialiser le code promo si les services changent
    this.resetPromoCode();
  }

  // Gestion du code promo
  setPromoCode(code: string) {
    this.promoCode = code;
  }

  resetPromoCode() {
    this.promoCode = '';
    this.promoCodeValidated = false;
    this.promoCodeDiscount = 0;
    this.promoCodeMessage = '';
  }

  async validatePromoCode() {
    if (!this.promoCode || !companyStore.getIdCompanySelectedDefault || this.selectedServices.length === 0) {
      return;
    }

    this.isValidatingPromoCode = true;
    this.promoCodeMessage = '';

    try {
      const result = await promoCodeViewModel.validatePromoCode({
        code: this.promoCode,
        clientId: 'manual-booking',
        companyId: companyStore.getIdCompanySelectedDefault,
        amount: this.totalPrice
      });

      if (result && result.isValid) {
        this.promoCodeValidated = true;
        this.promoCodeDiscount = result.discountAmount || 0;
        this.promoCodeMessage = `Code appliqué ! Réduction de ${this.promoCodeDiscount.toFixed(2)}€`;
      } else {
        this.promoCodeValidated = false;
        this.promoCodeDiscount = 0;
        this.promoCodeMessage = result?.message || 'Code promo invalide';
      }
    } catch (error) {
      console.error('Erreur lors de la validation du code promo:', error);
      this.promoCodeValidated = false;
      this.promoCodeDiscount = 0;
      this.promoCodeMessage = 'Erreur lors de la validation du code promo';
    } finally {
      this.isValidatingPromoCode = false;
    }
  }

  isFormValid(): boolean {
    // Vérifier qu'il y a au moins un service sélectionné
    if (this.selectedServices.length === 0) return false;

    // Vérifier le collaborateur
    if (!this.selectedCollaboratorCompagnieId) return false;

    // Vérifier la date
    if (!this.selectedDate) return false;

    // Vérifier les horaires manuels
    if (!this.manualStartTime || !this.manualEndTime) return false;

    // Vérifier les informations client
    if (!this.clientFirstName || !this.clientLastName || !this.clientEmail) return false;

    return true;
  }

  // Soumission du formulaire (booking manuel)
  async submitForm(): Promise<boolean> {
    if (!this.isFormValid()) {
      toastStore.error('Veuillez remplir tous les champs obligatoires.');
      return false;
    }

    this.setIsSubmitting(true);

    try {
      // Construire le tableau de services pour l'API
      const services = this.selectedServices.map(item => ({
        serviceId: item.service.id,
        optionIds: item.optionIds,
        price: item.service.price,
        duration: item.service.duration
      }));

      // Construire les dates/heures ISO
      const startDateTime = `${this.selectedDate}T${this.manualStartTime}:00`;
      const endDateTime = `${this.selectedDate}T${this.manualEndTime}:00`;

      const bookingData: BookingCreateParams = {
        services,
        startTime: new Date(startDateTime).toISOString(),
        endTime: new Date(endDateTime).toISOString(),
        firstName: this.clientFirstName,
        lastName: this.clientLastName,
        email: this.clientEmail,
        phone: this.clientPhone || undefined,
        notes: this.note || undefined,
        companyId: companyStore.getIdCompanySelectedDefault || undefined,
        collaboratorCompagnieId: this.selectedCollaboratorCompagnieId,
        promoCode: this.promoCodeValidated ? this.promoCode : undefined
      };

      let success: boolean;
      if (this.isEditMode && this.bookingIdToEdit) {
        // Mode édition
        success = await this.bookingViewModel.updateManualBooking(this.bookingIdToEdit, bookingData);
      } else {
        // Mode création
        success = await this.bookingViewModel.createManualBooking(bookingData);
      }

      if (success) {
        this.resetForm();
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire:', error);
      return false;
    } finally {
      this.setIsSubmitting(false);
    }
  }

  // Pré-remplir le formulaire en mode édition
  async loadBookingForEdit(booking: any) {
    this.isEditMode = true;
    this.bookingIdToEdit = booking.id;

    // Charger les informations client
    this.clientFirstName = booking.clientFirstName || booking.client?.firstName || '';
    this.clientLastName = booking.clientLastName || booking.client?.lastName || '';
    this.clientEmail = booking.clientEmail || booking.client?.email || '';
    this.clientPhone = booking.clientPhone || booking.client?.phone || '';
    this.note = booking.note || '';

    // Charger les horaires
    if (booking.startTime) {
      const startDate = new Date(booking.startTime);
      this.selectedDate = startDate.toISOString().split('T')[0];
      this.manualStartTime = startDate.toTimeString().substring(0, 5);
    }

    if (booking.endTime) {
      const endDate = new Date(booking.endTime);
      this.manualEndTime = endDate.toTimeString().substring(0, 5);
    }

    // Charger le collaborateur
    this.selectedCollaboratorCompagnieId = booking.collaboratorCompagnieId || '';

    // Charger les services sélectionnés
    if (booking.services && Array.isArray(booking.services)) {
      // Réinitialiser les services sélectionnés
      this.selectedServices = [];

      // Pour chaque service de la réservation
      for (const bookingService of booking.services) {
        try {
          // Récupérer les détails complets du service
          const serviceDetails = await this.serviceViewModel.getServiceById(bookingService.serviceId);

          if (serviceDetails) {
            // Ajouter le service à la liste des services sélectionnés
            this.selectedServices.push({
              service: serviceDetails,
              optionIds: bookingService.optionIds || []
            });
          }
        } catch (error) {
          console.error('Erreur lors du chargement du service:', bookingService.serviceId, error);
        }
      }
    }
  }

  resetForm() {
    this.selectedService = '';
    this.selectedCollaboratorCompagnieId = '';
    this.selectedOptions = {};
    this.selectedDate = '';
    this.selectedServices = [];
    this.manualStartTime = '';
    this.manualEndTime = '';
    this.clientFirstName = '';
    this.clientLastName = '';
    this.clientEmail = '';
    this.clientPhone = '';
    this.note = '';
    this.resetPromoCode();
    this.setServiceDetails(null);
    this.setOptionCategories([]);
    this.isEditMode = false;
    this.bookingIdToEdit = null;
  }

  // Getters calculés
  get isLoading(): boolean {
    return this.serviceViewModel.isLoading ||
           this.bookingViewModel.isLoading ||
           this.isSubmitting;
  }

  /**
   * Sélectionne un service et l'ajoute automatiquement au panier s'il n'a pas d'options
   */
  async selectAndAddService(serviceId: string) {
    this.selectedService = serviceId;
    this.selectedOptions = {};

    if (serviceId) {
      await this.loadServiceDetails(serviceId);

      // Si le service n'a pas d'options requises, l'ajouter automatiquement
      if (this.serviceDetails && this.optionCategories.length === 0) {
        this.addServiceToBooking();
      }
    } else {
      this.serviceDetails = null;
      this.optionCategories = [];
    }
  }
}

export const reservationViewModel = new ReservationViewModel();
